import "./App.css";
import { useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useGoogleAnalytics } from "utils";

import {
  Home,
  Login,
  MyPage,
  Notification,
  Setting,
  Post,
  Profile,
  Admin,
  Account,
  ServiceList,
  ServiceDetail,
  HostServiceForm,
  ServiceEditForm,
  Landing,
  Explore,
  SignupNormal,
} from "pages";
import { BaseLayout } from "layouts";
import AuthGuard from "components/common/AuthGuard";
import { AppModalProvider } from "components/common/AppModalProvider";
import {
  MyPageSettingComponent,
  FAQComponent,
  ContactComponent,
  LogOutComponent,
  DeleteAccountComponent,
  MyPageComponent,
  EditProfileComponent,
  FAQManageComponent,
  AdminLoginComponent,
  ReportManageComponent,
  WithdrawManageComponent,
  DashboardComponent,
  UserManageComponent,
  ServiceManageComponent,
  BookingManageComponent,
  PaymentManageComponent,
  AccountWithdrawn,
  AccountBlock,
  BookingListComponent,
  FloatingCreateButton,
} from "components";
import PaymentSuccess from "pages/Payment/PaymentSuccess";
import PaymentFail from "pages/Payment/PaymentFail";
import PrivacyPage from "pages/Privacy/Privacy";
import { useUserStore, useWalletStore } from "store";
import { getMyWallet, getProfile } from "apis";

function App() {
  const navigate = useNavigate();
  const { setUser } = useUserStore();
  const { setWallet } = useWalletStore();

  useGoogleAnalytics();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");
    const termsAgree = queryParams.get("termsAgree");

    // Skip auth check for public pages
    if (
      window.location.pathname.startsWith("/guest") ||
      window.location.pathname.startsWith("/account") ||
      window.location.pathname.startsWith("/explore") ||
      window.location.pathname.startsWith("/service") ||
      window.location.pathname.startsWith("/signup") ||
      window.location.pathname.startsWith("/payment") ||
      window.location.pathname === "/privacy"
    ) {
      return;
    }

    if (window.location.pathname.startsWith("/admin")) {
      if (token) {
        localStorage.setItem("admin_access_token", token);
      }

      if (!localStorage.getItem("admin_access_token")) {
        navigate("/admin/login");
      }
    } else {
      if (token) {
        localStorage.setItem("user_access_token", token);
        if (termsAgree === "true") {
          localStorage.setItem("terms_agree", termsAgree);
        }
        navigate("/");
      }

      // Load user data if authenticated
      if (
        localStorage.getItem("user_access_token") &&
        localStorage.getItem("terms_agree")
      ) {
        getProfile().then((response) => {
          setUser(response.data.data);
          getMyWallet().then((response) => {
            setWallet(response.data.data);
          });
        });
      }
    }

  }, [navigate]);

  const isLoggedIn = !!localStorage.getItem("user_access_token") && !!localStorage.getItem("terms_agree");

  return (
    <AppModalProvider>
    <BaseLayout>
      <Routes>
        {/* ─── Public Routes ─── */}
        <Route path="/" element={isLoggedIn ? <Home /> : <Landing />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Login />} />
        <Route path="/signup/normal" element={<Login />} />
        <Route path="/service" element={<ServiceList />} />
        <Route path="/service/:id" element={<ServiceDetail />} />
        <Route path="/guest" element={<Explore />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/fail" element={<PaymentFail />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* ─── Account Status ─── */}
        <Route path="/account" element={<Account />}>
          <Route path="withdrawn" element={<AccountWithdrawn />} />
          <Route path="blocked" element={<AccountBlock />} />
        </Route>

        {/* ─── Protected Routes ─── */}
        <Route path="/:profileId" element={<AuthGuard><Profile /></AuthGuard>} />
        <Route path="/mypage" element={<AuthGuard><MyPage /></AuthGuard>}>
          <Route path="" element={<MyPageComponent />} />
          <Route path="edit-profile" element={<EditProfileComponent />} />
          <Route path="bookings" element={<BookingListComponent />} />
        </Route>
        <Route path="/notification" element={<AuthGuard><Notification /></AuthGuard>} />
        <Route path="/setting" element={<AuthGuard><Setting /></AuthGuard>}>
          <Route path="mypage" element={<MyPageSettingComponent />} />
          <Route path="faq" element={<FAQComponent />} />
          <Route path="contact" element={<ContactComponent />} />
          <Route path="logout" element={<LogOutComponent />} />
          <Route path="delete-account" element={<DeleteAccountComponent />} />
        </Route>
        <Route path="/post/:id" element={<AuthGuard><Post /></AuthGuard>} />
        <Route path="/service/new" element={<AuthGuard><HostServiceForm /></AuthGuard>} />
        <Route path="/service/:id/edit" element={<AuthGuard><ServiceEditForm /></AuthGuard>} />

        {/* ─── Admin ─── */}
        <Route path="/admin" element={<Admin />}>
          <Route path="" element={<DashboardComponent />} />
          <Route path="login" element={<AdminLoginComponent />} />
          <Route path="user" element={<UserManageComponent />} />
          <Route path="service" element={<ServiceManageComponent />} />
          <Route path="booking" element={<BookingManageComponent />} />
          <Route path="payment" element={<PaymentManageComponent />} />
          <Route path="faq" element={<FAQManageComponent />} />
          <Route path="report" element={<ReportManageComponent />} />
          <Route path="withdraw" element={<WithdrawManageComponent />} />
        </Route>
    </Routes>
    </BaseLayout>
    </AppModalProvider>
  );
}

export default App;
